/**
 * flyPage.js
 *
 * Small things every Flight YJ030 page shares.
 */

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Keeps search engines away while the page is open, and sets the tab's title
 * @param {String} title    -   e.g. 'Check-in · Flight YJ030'
 */
export const useNoIndex = (title = "Flight YJ030 · Right Brother Airways") => {
    useEffect(() => {
        const meta = document.createElement("meta");
        meta.name = "robots";
        meta.content = "noindex, nofollow";
        document.head.appendChild(meta);
        const previousTitle = document.title;
        document.title = title;
        return () => {
            meta.remove();
            document.title = previousTitle;
        };
    }, [title]);
};

/**
 * A time as the party sees it, in IST
 * @param {String} iso      -   e.g. '2026-10-30T15:01:00+00:00'
 * @returns {String}        -   e.g. '20:31'
 */
export const formatTime = (iso) => new Date(iso).toLocaleTimeString("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false });

/**
 * A key (staff or admin) typed once and remembered on this device
 * @param {String} name     -   localStorage key, e.g. 'yj030-staff-key'
 * @returns {Array}         -   [key, save(key), forget()]
 */
export const useStoredKey = (name) => {
    const [key, setKey] = useState(() => {
        try {
            return localStorage.getItem(name) || "";
        } catch {
            return "";
        }
    });
    const save = useCallback((value) => {
        setKey(value);
        try {
            localStorage.setItem(name, value);
        } catch {
            // Private mode: it just won't be remembered
        }
    }, [name]);
    const forget = useCallback(() => {
        setKey("");
        try {
            localStorage.removeItem(name);
        } catch {
            // Nothing to forget
        }
    }, [name]);
    return [key, save, forget];
};

/**
 * The trial run, at /fly2: the same pages as the real flight, on the backend's trial data (its API is under /trial)
 */
export const isTrial = () => window.location.pathname.startsWith("/fly2");

/**
 * A page's path on this run: '/fly/check-in' is '/fly2/check-in' in the trial
 * @param {String} path     -   e.g. '/fly/immigration'
 */
export const flyPath = (path) => (isTrial() ? path.replace(/^\/fly/, "/fly2") : path);

/**
 * Milliseconds until a time, ticking every second while running
 * @param {String} until    -   ISO time, e.g. when guessing closes
 * @param {Boolean} running -   Only ticks while true
 * @returns {Number|null}   -   null without a time
 */
export const useTimeLeft = (until, running) => {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (!running) return undefined;
        setNow(Date.now());
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [running]);
    return until ? new Date(until).getTime() - now : null;
};

/**
 * A duration as HH:MM:SS, e.g. '01:04:09' (never negative)
 * @param {Number} ms   -   Milliseconds
 */
export const formatHms = (ms) => {
    const seconds = Math.max(0, Math.floor(ms / 1000));
    return [Math.floor(seconds / 3600), Math.floor((seconds % 3600) / 60), seconds % 60].map((part) => String(part).padStart(2, "0")).join(":");
};

// A barcode scanner types the whole code in a burst: keys this close together, and at least this many, are a scan
// rather than someone typing. Most scanners press Enter (or Tab) at the end; one set up without either is done once
// the burst stops.
const SCANNER_KEY_GAP_MS = 80;
const SCANNER_IDLE_MS = 250;
const MIN_SCAN_LENGTH = 6;

/**
 * A USB or Bluetooth 2D scanner (it acts as a keyboard): calls onCode with each code scanned anywhere on the page,
 * so nobody has to click into a box first. Keys typed into a text box are left to that box.
 * @param {Function} onCode     -   Called with the scanned text, e.g. a pass's serial or a PNR
 * @param {Boolean} enabled     -   Only listens while true
 */
export const useBarcodeScanner = (onCode, enabled = true) => {
    const onCodeRef = useRef(onCode);
    onCodeRef.current = onCode;
    useEffect(() => {
        if (!enabled) return undefined;
        let buffer = "";
        let lastKeyAt = 0;
        let idleTimer;
        const finish = () => {
            clearTimeout(idleTimer);
            if (buffer.length >= MIN_SCAN_LENGTH) onCodeRef.current(buffer);
            buffer = "";
        };
        const onKey = (e) => {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.ctrlKey || e.metaKey || e.altKey) return;
            const now = Date.now();
            if (now - lastKeyAt > SCANNER_KEY_GAP_MS) buffer = "";
            lastKeyAt = now;
            if (e.key === "Enter" || e.key === "Tab") {
                if (buffer.length >= MIN_SCAN_LENGTH) e.preventDefault();
                finish();
            } else if (e.key.length === 1) {
                buffer += e.key;
                clearTimeout(idleTimer);
                idleTimer = setTimeout(finish, SCANNER_IDLE_MS);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => {
            clearTimeout(idleTimer);
            window.removeEventListener("keydown", onKey);
        };
    }, [enabled]);
};

/**
 * A short beep: rising for a check-in, low for a problem. Needs an AudioContext made during a tap (iPhone).
 */
export const beep = (audio, ok) => {
    if (!audio) return;
    try {
        const notes = ok ? [880, 1320] : [220, 180];
        notes.forEach((frequency, i) => {
            const oscillator = audio.createOscillator();
            const gain = audio.createGain();
            const start = audio.currentTime + i * 0.12;
            oscillator.frequency.value = frequency;
            gain.gain.setValueAtTime(0.25, start);
            gain.gain.exponentialRampToValueAtTime(0.001, start + 0.11);
            oscillator.connect(gain).connect(audio.destination);
            oscillator.start(start);
            oscillator.stop(start + 0.12);
        });
    } catch {
        // No sound is fine
    }
};
